import { isSpectator, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MeetingSessionHighlightingOnResponseActionHandler, MeetingSessionHighlightingState, SocketActionHandler } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMeetingUtilsContext } from "../contexts/meetingUtils/context";
import { isExpert } from "../helpers/roles";
import { HighlightedSessions } from "../interfaces/highlight";
import { toastError, toastInfo, toastSuccess } from "../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useSdkContext } from "../contexts/sdk/context";
import { useFeatures } from "./features";
import { buildMediasFromSources, MeetingSourceExtended, useMeetingSources } from "./meeting";

export const useHighlightSessions = (params?: { onNewRequests?: () => void }): HighlightedSessions => {
  const { populated, containerAPI } = useMeetingUtilsContext();
  const [sessionId] = useMeetingSelector(containerAPI, "session", s => s?.id);

  const seenRequests = useRef(new Set<string>());

  const confirmedSessionsSet = useMemo(() => {
    const set = new Set<string>();
    populated.activity?.confirmedSessions?.forEach(s => set.add(s.id));
    return set;
  }, [populated.activity?.confirmedSessions]);

  const sessions = useMemo(() => {
    const output: HighlightedSessions = {
      ongoing: [],
      requesting: [],
      inactive: [],
    };

    populated.users?.forEach((pUser) => {
      pUser.sessions?.forEach((incSession) => {
        if (isExpert(incSession.session)) return;
        if (incSession.id === sessionId) return;
        switch (incSession.session.highlighting) {
          case MeetingSessionHighlightingState.PENDING_REQUEST:
            output.requesting.push(incSession);
            break;
          case MeetingSessionHighlightingState.ONGOING:
            const pending = !confirmedSessionsSet.has(incSession.id) || !incSession.session.medias.length;
            output.ongoing.push({ session: incSession, pending });
            break;
          default:
            output.inactive.push(incSession);
        }
      });
    });

    return output;
  }, [populated.users, confirmedSessionsSet, sessionId]);

  useEffect(() => {
    if (!params?.onNewRequests) return;

    const newRequests = new Set<string>();
    sessions.requesting.forEach(s => {
      newRequests.add(s.id);
    });

    // check if there is some requests that were not in the previous seen
    
    let hasNewRequests = false;
    newRequests.forEach(id => {
      if (!seenRequests.current.has(id)) hasNewRequests = true;
    });
    if (hasNewRequests) params.onNewRequests();

    seenRequests.current = newRequests;
    
  }, [sessions.requesting]);

  return sessions;
};

export interface HighlightMediaSelectionState {
  visible: boolean;
  remainingSeconds?: number;
  onConfirm: () => Promise<void>;
  onCancel: () => Promise<void>;
}

export const useHighlightNotifications = (): HighlightMediaSelectionState => {
  const { populated, containerAPI } = useMeetingUtilsContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const [meetingId] = useMeetingSelector(containerAPI, "meeting", meeting => meeting?.id);

  const sdkCtx = useSdkContext();
  const sdkCtxRef = useRefAutoUpdate(sdkCtx);
  const features = useFeatures();
  const sources = useMeetingSources();
  const containerAPIRef = useRefAutoUpdate(containerAPI);

  const [showMediaSelection, setShowMediaSelection] = useState(false);

  const selfActivity = useMemo(() => {
    if (!session) return undefined;

    // No ongoing activity
    if (!populated.activity) return undefined;

    // I am not concerned by the activity
    if (!populated.activity.sessions.some(s => s.id === session.id)) return undefined;

    return {
      // I confirmed my participation
      activity: populated.activity,
      confirmed: populated.activity.confirmedSessions.some(s => s.id === session.id),
    };
  }, [populated.activity, session]);

  const iAmRequestingRef = useRef(session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST);
  const onBoardRef = useRef(selfActivity?.confirmed ?? false);

  ////////////////// UTILS //////////////////

  const onJoinActivity = useCallback(async () => {
    if (!meetingId || !session) return;
    const api = containerAPIRef.current;

    // If spectator with no media, create media on the session before enlisting
    if (api && isSpectator(session) && (!session.medias || session.medias.length === 0)) {
      const medias = buildMediasFromSources((sources ?? []) as MeetingSourceExtended[]);
      for (const media of medias) {
        await api.updater.medias.create(session.id, media);
      }
    }

    const client = sdkCtxRef.current.client.getWebsocketClient();
    
    // Maybe the backend already auto enlisted me (<30 sec) anyway this is useless now
    if (!populated.activity?.confirmedSessions.some(s => s.id === session.id)) {
      const response = await client.emitter.meeting.enlistInActivityRoom({
        id: meetingId,
      });
      if (response.error || !response.response) {
        toastError("Échec de l'intervention");
        return;
      }
    }
    
    setShowMediaSelection(false);
  }, [meetingId, session, containerAPIRef, sdkCtxRef, populated.activity?.confirmedSessions, sources]);

  const onRemoveHighlight = useCallback(async () => {
    if (!session) return;
    const client = sdkCtxRef.current.client.getWebsocketClient();
    const response = await client.emitter.meeting.stopHighlighting({
      sessionId: session.id,
    });
    if (response.error || !response.response) {
      toastError("Une erreur est survenue");
      return;
    }
    setShowMediaSelection(false);
  }, [session]);

  ////////////////// HOOKS //////////////////

  useEffect(() => {
    if (session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) {
      iAmRequestingRef.current = true;
    }
  }, [session?.highlighting]);

  useEffect(() => {
    if (selfActivity?.confirmed) {
      if (iAmRequestingRef.current) {
        toastSuccess("Votre demande d'intervention a été acceptée");
      } else {
        toastSuccess("Confirmation de votre intervention");
      }
      onBoardRef.current = true;
    } else {
      if (onBoardRef.current) {
        toastInfo("Fin de votre intervention");
        onBoardRef.current = false;
      }
    }
    iAmRequestingRef.current = false;
  }, [selfActivity?.confirmed]);

  useEffect(() => {
    const client = sdkCtxRef.current.client.getWebsocketClient();

    const handlers: SocketActionHandler[] = [];

    const responseHandler = new MeetingSessionHighlightingOnResponseActionHandler();
    responseHandler.onReceive = (event) => {
      if (!event.content.accepted) {
        toastInfo("Votre demande d'intervention a été refusée");
      }
    }
    handlers.push(responseHandler);

    client.registerActionHandlers(handlers);
    return () => client.unregisterActionHandlers(handlers);
  }, []);

  const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const onJoinActivityRef = useRefAutoUpdate(onJoinActivity);

  // Highlighted but no media = needs to set up sources before going on board
  const highlightedWithoutMedia = session?.highlighting === MeetingSessionHighlightingState.ONGOING
    && (!session.medias || session.medias.length === 0);

  // Show media selection when:
  // - invited to activity and not yet confirmed (normal popup flow)
  // - OR highlighted but has no media (auto-enlisted by backend, needs source setup)
  const shouldShow = (selfActivity && !selfActivity.confirmed) || !!highlightedWithoutMedia;

  useEffect(() => {
    if (shouldShow) {
      setShowMediaSelection(true);
    } else {
      setShowMediaSelection(false);
      setRemainingSeconds(undefined);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [shouldShow]);

  // Auto-confirm timer (optional via feature flag)
  useEffect(() => {
    if (!showMediaSelection) return;
    const duration = features.highlightAutoAcceptTimer;
    if (duration === undefined) return;

    setRemainingSeconds(duration);
    timerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev === undefined) return undefined;
        const next = prev - 1;
        if (next <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          onJoinActivityRef.current();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [showMediaSelection, features.highlightAutoAcceptTimer]);

  return {
    visible: showMediaSelection,
    remainingSeconds,
    onConfirm: onJoinActivity,
    onCancel: onRemoveHighlight,
  };
};
